import { FC } from 'react'

import { TypedDocumentNode } from '@apollo/client'
import {
  ProcurementPurchaseOrderFilter,
  PurchaseOrdersClosedWidgetDocument,
  PurchaseOrdersClosedWidgetQuery,
  PurchaseOrdersTotalWidgetDocument,
  PurchaseOrdersTotalWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface QueryArgsType {
  currency: string
  companyId: string
  purchaseOrderFilters?: ProcurementPurchaseOrderFilter
  search?: string
  vendorId?: string
}

type WidgetConfig<TQuery> = {
  id: string
  document: TypedDocumentNode<TQuery, QueryArgsType>
  prepareData: (data: TQuery) => { groupedChartData: ChartData[] }
}

const getLabelByStatus = (status: string) => {
  switch (status) {
    case 'closed':
      return i18n.t('procurement.purchase_orders.status.closed')
    case 'total':
      return i18n.t('finance.overview.purchase_orders.total_po')
    default:
      return status
  }
}

const createPrepareData =
  (status: string) => (data: PurchaseOrdersClosedWidgetQuery | PurchaseOrdersTotalWidgetQuery) => {
    const count = data?.procurement?.purchaseOrdersConnection?.aggregate?.count || 0

    const groupedChartData: ChartData[] = [
      {
        status,
        label: getLabelByStatus(status),
        value: count,
        formattedValue: i18n.formatNumber(count),
      },
    ]

    return { groupedChartData }
  }

const createWidgetBuilder = <TQuery,>({ id, document, prepareData }: WidgetConfig<TQuery>) => {
  return ({
    queryArgs,
    filters,
  }: {
    queryArgs: QueryArgsType
    filters: ProcurementPurchaseOrderFilter
  }) => {
    const WidgetComponent: FC<{ data: ReturnType<typeof prepareData> }> = ({ data }) => {
      const { groupedChartData } = data

      return (
        <FinanceWidget
          id={id}
          type={SupportedCharts.COUNTERS}
          showChart={false}
          countersChartDatas={groupedChartData}
          chartDatas={groupedChartData}
        />
      )
    }

    const filteredPurchaseOrdersFilters = removeNullBetween(filters)

    return buildWidget({
      id,
      component: WidgetComponent,
      document,
      prepareData,
      queryArgs: {
        ...queryArgs,
        purchaseOrderFilters: { ...filteredPurchaseOrdersFilters },
      },
    })
  }
}

export const buildPurchaseOrdersClosedCounterInsightWidget = createWidgetBuilder({
  id: 'purchaseOrdersClosedWidget',
  document: PurchaseOrdersClosedWidgetDocument,
  prepareData: createPrepareData('closed'),
})

export const buildPurchaseOrdersTotalCounterInsightWidget = createWidgetBuilder({
  id: 'purchaseOrdersTotalWidget',
  document: PurchaseOrdersTotalWidgetDocument,
  prepareData: createPrepareData('total'),
})
